import { useMemo, useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { DEFAULT_TIMEZONE } from '../../../../constants/appointments';
import useUser from '../../../../hooks/global/useUser';
import { Appointment, LoadedUser } from '../../../../interfaces';
import useGetCustomerAppointmentsTab from '../../../../hooks/customers/useGetCustomerAppointmentsTab';
import AppointmentSideBarModal from '../../../appointments/AppointmentSideBarModal';
import FullPageModal from '../../../modal/FullPageModal';
import AppointmentForm from '../../../form/AppointmentForm';
import AppointmentsTable from './AppointmentsTable';
import ShowAllLink from './ShowAllLink';
import { formatAppointmentsSummaryLine } from './helpers/appointmentRowContent';

interface Props {
  customerId: string;
  businessId: string;
}

function AppointmentsTab({ customerId, businessId }: Props) {
  const { user } = useUser<LoadedUser>();
  const now = useMemo(() => new Date(), []);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<
    string | null
  >(null);
  const [editingAppointment, setEditingAppointment] =
    useState<Appointment | null>(null);
  const {
    appointments,
    summary,
    isInitialLoading,
    isFetching,
    hasMore,
    loadMore,
    isError,
    isFetchNextPageError,
    refetch,
  } = useGetCustomerAppointmentsTab({ customerId, businessId });

  const onCloseAppointmentForm = () => {
    setEditingAppointment(null);
    refetch();
  };

  if (isInitialLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: '200px' }}>
        <CircularProgress color="primary" />
      </Stack>
    );
  }

  if (isError || !summary) {
    return (
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        There was an error loading this customer&apos;s appointments.
      </Typography>
    );
  }

  if (appointments.length === 0) {
    return (
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        No appointments yet.
      </Typography>
    );
  }

  return (
    <>
      <Typography
        variant="body1"
        sx={{ color: 'text.secondary', marginBottom: '28px' }}>
        {formatAppointmentsSummaryLine(summary)}
      </Typography>
      <AppointmentsTable
        appointments={appointments}
        timezone={user?.timezone ?? DEFAULT_TIMEZONE}
        now={now}
        onSelectAppointment={appointment =>
          setSelectedAppointmentId(appointment.appointmentId)
        }
      />
      {isFetchNextPageError && (
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', marginTop: '28px' }}>
          There was an error loading more appointments.
        </Typography>
      )}
      {hasMore && (
        <ShowAllLink
          label={isFetchNextPageError ? 'Retry' : 'Show more'}
          isLoading={isFetching}
          onClick={loadMore}
        />
      )}
      {!!selectedAppointmentId && (
        <AppointmentSideBarModal
          appointmentId={selectedAppointmentId}
          isOpen
          onClose={() => setSelectedAppointmentId(null)}
          onEdit={setEditingAppointment}
          onRefetchAppointments={() => {
            refetch();
          }}
        />
      )}
      <FullPageModal
        modalOpen={!!editingAppointment}
        setModalOpen={onCloseAppointmentForm}
        handleClose={onCloseAppointmentForm}
        name="Edit Appointment">
        <AppointmentForm
          selectedAppointment={editingAppointment || undefined}
          onClose={onCloseAppointmentForm}
        />
      </FullPageModal>
    </>
  );
}

export default AppointmentsTab;
